import { MiddlewareAPI, Dispatch, PayloadAction, Middleware } from '@reduxjs/toolkit';
import { UrlUtils } from 'src/utils/index';
import { IRoom, DateSearchTypeEnum } from 'src/common-types/index';
import {
  ROOMS_LABEL,
  LOCATION_LABEL,
  LOCATION_OBJECT_LABEL,
  DATES_LABEL,
  SESSION_KEY_LABEL,
  CONDO_SESSION_KEY,
  SESSION_KEY_LABEL_ROOMS,
  CONDO_LOCATIONS_OBJECT_LABEL,
  CONDO_LOCATIONS_LABEL,
  CONDO_DATES_LABEL,
  CONDO_DATES_FLEXIBLE_LABEL,
  CONDO_GUESTS_LABEL,
  CONDO_IS_FLEXIBLE_LABEL,
  MAX_SELECTED_MONTH_COUNT,
  CONDO_GUESTS_ACTIONS_LABEL,
  CONDO_SEARCH_BOUNDS,
} from 'src/constants/index';
import { RootState } from '../index';
import {
  locationActions,
  datesActions,
  hotelsActions,
  roomsSearchActions,
  condosLocationPickerActions,
  condoStrictDatesActions,
  condoFlexibleDatePickerActions,
  condosActions,
} from '../slices';

const zero = 0;
const one = 1;

export const urlMiddleware: Middleware = (api: MiddlewareAPI<Dispatch, RootState>) => {
  return (next: Dispatch<PayloadAction<Record<string, unknown> | string | number | undefined>>) => {
    return (action: PayloadAction<Record<string, unknown> | string | number | undefined>) => {
      const { getState } = api;
      const { condoFlexibleDatePickerStore } = getState();

      switch (action.type) {
        case locationActions.selectLocation.toString(): {
          UrlUtils.setUrl(LOCATION_OBJECT_LABEL, action.payload as Record<string, unknown>);
          break;
        }
        case locationActions.selectLocationLabel.toString(): {
          UrlUtils.setUrl(LOCATION_LABEL, action.payload as Record<string, unknown>);
          break;
        }
        case condosActions.setBounds.toString(): {
          UrlUtils.setUrl(CONDO_SEARCH_BOUNDS, action.payload as Record<string, unknown>);
          break;
        }
        case datesActions.setDates.toString(): {
          UrlUtils.setUrl(DATES_LABEL, action.payload as Record<string, unknown>);
          break;
        }
        case hotelsActions.setSessionKey.toString(): {
          UrlUtils.setUrl(SESSION_KEY_LABEL, action.payload as Record<string, unknown>);
          break;
        }
        case condosActions.setSessionKey.toString(): {
          UrlUtils.setUrl(CONDO_SESSION_KEY, action.payload as Record<string, unknown>);
          break;
        }
        case roomsSearchActions.setSessionKey.toString(): {
          UrlUtils.setUrl(SESSION_KEY_LABEL_ROOMS, action.payload as Record<string, unknown>);
          break;
        }
        case condosLocationPickerActions.selectLocation.toString(): {
          UrlUtils.setUrl(CONDO_LOCATIONS_OBJECT_LABEL, action.payload as Record<string, unknown>);
          break;
        }
        case condosLocationPickerActions.selectLocationLabel.toString(): {
          UrlUtils.setUrl(CONDO_LOCATIONS_LABEL, action.payload as Record<string, unknown>);
          break;
        }
        case condoStrictDatesActions.setDates.toString(): {
          UrlUtils.setUrl(CONDO_DATES_LABEL, action.payload as Record<string, unknown>);
          UrlUtils.setUrl(CONDO_IS_FLEXIBLE_LABEL, zero);
          break;
        }
        case condoStrictDatesActions.resetDates.toString(): {
          if (action.payload) {
            UrlUtils.removeFromUrl(CONDO_DATES_LABEL);
            UrlUtils.setUrl(CONDO_IS_FLEXIBLE_LABEL, zero);
          }
          break;
        }
        case condoFlexibleDatePickerActions.resetState.toString(): {
          UrlUtils.removeFromUrl(CONDO_DATES_FLEXIBLE_LABEL);
          UrlUtils.setUrl(CONDO_IS_FLEXIBLE_LABEL, zero);
          break;
        }
        case condoStrictDatesActions.setSearchType.toString(): {
          UrlUtils.setUrl(
            CONDO_IS_FLEXIBLE_LABEL,
            action.payload === DateSearchTypeEnum.Flexible ? one : zero,
          );
          break;
        }
        case condoFlexibleDatePickerActions.setStayFor.toString(): {
          UrlUtils.setUrl(CONDO_DATES_FLEXIBLE_LABEL, {
            ...condoFlexibleDatePickerStore,
            stayFor: action.payload,
          });
          UrlUtils.setUrl(CONDO_IS_FLEXIBLE_LABEL, one);
          break;
        }
        case condoFlexibleDatePickerActions.selectMonths.toString(): {
          let selectedMonths: string[] = condoFlexibleDatePickerStore.selectedMonths.slice();

          if (condoFlexibleDatePickerStore.selectedMonths.includes(action.payload as string)) {
            selectedMonths = condoFlexibleDatePickerStore.selectedMonths.filter(
              (month: string) => month !== action.payload,
            );
          } else {
            if (condoFlexibleDatePickerStore.selectedMonths.length < MAX_SELECTED_MONTH_COUNT) {
              selectedMonths.push(action.payload as string);
            }
          }

          UrlUtils.setUrl(CONDO_DATES_FLEXIBLE_LABEL, {
            ...condoFlexibleDatePickerStore,
            selectedMonths,
          });
          UrlUtils.setUrl(CONDO_IS_FLEXIBLE_LABEL, one);
          break;
        }
        default:
          break;
      }

      if (action.type.includes(CONDO_GUESTS_ACTIONS_LABEL)) {
        setTimeout(() => {
          UrlUtils.setUrl(CONDO_GUESTS_LABEL, { ...getState().condoGuestsStore });
        });
      }

      if (action.type.includes(ROOMS_LABEL)) {
        setTimeout(() => {
          UrlUtils.setUrl(
            ROOMS_LABEL,
            getState().roomsStore.rooms.map((room: IRoom) => {
              return {
                adultsCount: room.adultsCount,
                kids: room.kids.map(({ age }) => age),
              };
            }),
          );
        });
      }

      return next(action);
    };
  };
};
